import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";

import clubStore from "../../storage/ClubStore";
import appStore from "../../storage/AppStore";
import systemStore from "../../storage/SystemStore";

import Btn from "../../ui/Btn";
import CompanySearch from "./../CompanySearch";

import "../../common/game.css";
import {Accordion, AccordionTab} from "primereact/accordion";

const safeArray = (v) => (Array.isArray(v) ? v : []);

const CompetitionCompanies = ({ role = "MGR" }) => {
  const { id } = useParams();

  const competitionId = Number(id);
  const competition = clubStore.tournament.competition;
  const myCompanyId = clubStore.club?.id;

  const isManager = role === "MGR";
  const isReadonly = !isManager;
  const isHostCompany = Number(competition?.company_id) === Number(myCompanyId);

  const companies = safeArray(
    clubStore.tournament.tournamentCompanies || competition?.companies || []
  );

  useEffect(() => {
    if (!competitionId) return;

    const load = async () => {
      try {
        await clubStore.tournament.getCompetition(competitionId);
        await clubStore.tournament.getTournamentCompanies(competitionId);
      } catch (err) {
        console.error(err);
      }
    };

    load();
  }, [competitionId]);

  const invitedCount = companies.length;
  const acceptedCount = companies.filter((x) => !!x.agreed).length;
  const pendingCount = companies.filter((x) => !x.agreed).length;

  const handleInvite = async (company) => {
    if (!competitionId || !company?.id) return;

    try {
      const ret = await clubStore.tournament.inviteCompanyToTournament(
        competitionId,
        company.id
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.invitation_sent || "Invitation sent");
        await clubStore.tournament.getTournamentCompanies(competitionId);
      }
    } catch (err) {
      systemStore.setError(err?.message || "Invite failed");
    }
  };

  const handleAccept = async (item) => {
    try {
      const ret = await clubStore.tournament.acceptTournamentInvitation(
        competitionId,
        item.company_id
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(
          appStore.tr.invitation_accepted || "Invitation accepted"
        );
        await clubStore.tournament.getTournamentCompanies(competitionId);
      }
    } catch (err) {
      systemStore.setError(err?.message || "Accept failed");
    }
  };

  const handleRemove = async (item) => {
    if (!window.confirm(appStore.tr.are_you_sure_delete || "Are you sure?")) {
      return;
    }

    try {
      const ret = await clubStore.tournament.removeCompanyFromTournament(
        competitionId,
        item.company_id
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(
          appStore.tr.invitation_removed || "Invitation removed"
        );
        await clubStore.tournament.getTournamentCompanies(competitionId);
      }
    } catch (err) {
      systemStore.setError(err?.message || "Remove failed");
    }
  };

  const getCompanyName = (item) => item?.company?.name || item?.name || "-";

  const renderStatusBadge = (item) => {
    if (item?.agreed) {
      return (
        <span className="badge badge-finished">
          {appStore.tr.accepted || "Accepted"}
        </span>
      );
    }

    return (
      <span className="badge badge-upcoming">
        {appStore.tr.invited || "Invited"}
      </span>
    );
  };

  const canAccept = (item) => {
    if (!isManager) return false;
    if (item?.agreed) return false;
    return true;
  };

  const canRemove = (item) => {
    if (!isManager) return false;
    if (item?.agreed) return false;
    return true;
  };

  const renderCompanyCard = (item) => (
    <div key={item.id || item.company_id} className="col-12 lg:col-6">
      <div className="game-card h-full flex flex-column">
        <div className="flex align-items-start flex-wrap gap-3 mb-3">
          {item?.company?.logo ? (
            <img src={item.company.logo} alt={getCompanyName(item)} className="competition-company-logo" />
          ) : (
            <div className="competition-company-logo flex align-items-center justify-content-center">
              <i className="pi pi-building text-primary" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="font-semibold text-lg line-height-3">{getCompanyName(item)}</div>
            {item?.company?.country_text && (
              <div className="competition-company-sub flex align-items-center gap-2">
                <i className="pi pi-map-marker" />
                <span>{item.company.country_text}</span>
              </div>
            )}
          </div>

          {renderStatusBadge(item)}
        </div>

        <div className="competition-detail-item flex align-items-start gap-2 mb-3">
          <i className={`pi ${item?.agreed ? "pi-check-circle text-green-500" : "pi-clock text-600"} mt-1`} />
          <div className="text-sm line-height-3">
            {item?.agreed_by?.title
              ? `${appStore.tr.accepted_by || "Accepted by"}: ${item.agreed_by.title}`
              : item?.agreed
                ? (appStore.tr.accepted || "Accepted")
                : (appStore.tr.invited_waiting_response || "Invited, waiting response")}
          </div>
        </div>

        {!item?.agreed && item?.invitation_result !== '' && (
          <div className="game-badges mb-3">
            <span className="badge badge-warning">
              <i className="pi pi-exclamation-triangle" />
              {item.invitation_result}
            </span>
          </div>
        )}

        {(canAccept(item) || canRemove(item)) && (
          <div className="game-card-actions justify-content-end mt-auto">
            {canAccept(item) && (
              <Btn icon="pi pi-check" label={appStore.tr.accept || "Accept"} severity="success"
                   onClick={() => handleAccept(item)} />
            )}
            {canRemove(item) && (
              <Btn icon="pi pi-trash" label={appStore.tr.delete || "Delete"} severity="danger" outlined
                   onClick={() => handleRemove(item)} />
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="col-12">
      <div className="game-page mb-3">
        <div className="flex align-items-center gap-3 mb-3">
          <i className="pi pi-building text-primary text-2xl" />
          <div className="min-w-0">
            <div className="text-lg font-semibold">
              {appStore.tr.competition_companies || appStore.tr.clubs || "Clubs"}
            </div>
            <div className="text-sm text-600 mt-1">{competition?.name || "-"}</div>
          </div>
        </div>

        <div className="grid">
          <div className="col-12 sm:col-4">
            <div className="competition-detail-item h-full">
              <div className="competition-detail-label flex align-items-center gap-2">
                <i className="pi pi-send" />
                {appStore.tr.invited || "Invited"}
              </div>
              <div className="competition-detail-value text-xl">{invitedCount}</div>
            </div>
          </div>
          <div className="col-12 sm:col-4">
            <div className="competition-detail-item h-full">
              <div className="competition-detail-label flex align-items-center gap-2">
                <i className="pi pi-check-circle text-green-500" />
                {appStore.tr.accepted || "Accepted"}
              </div>
              <div className="competition-detail-value text-xl">{acceptedCount}</div>
            </div>
          </div>
          <div className="col-12 sm:col-4">
            <div className="competition-detail-item h-full">
              <div className="competition-detail-label flex align-items-center gap-2">
                <i className="pi pi-clock" />
                {appStore.tr.pending || "Pending"}
              </div>
              <div className="competition-detail-value text-xl">{pendingCount}</div>
            </div>
          </div>
        </div>
      </div>

      {isManager && isHostCompany && !isReadonly && (
        <div className="mb-3">
          <Accordion>
            <AccordionTab header={appStore.tr.invite_companies}>
              <CompanySearch sport_id={competition?.sport_id}
                             excludeIds={companies.map((x) => x.company_id)}
                             title={appStore.tr.company_search || "Company Search"}
                             onSelect={handleInvite} />
            </AccordionTab>
          </Accordion>
        </div>
      )}

      {!companies.length ? (
        <div className="game-page p-4 text-center">
          <i className="pi pi-building text-2xl text-600 mb-3" />
          <div className="text-600">{appStore.tr.no_companies_yet || "No companies yet"}</div>
        </div>
      ) : (
        <div className="grid">
          {companies.map((item) => renderCompanyCard(item))}
        </div>
      )}
    </div>
  );
};

export default observer(CompetitionCompanies);